import { FC, useState } from 'react'
import { Checkbox, Form, FormItem, TextArea } from '@lmc/ui-kit'
import { FormInstance, Select } from 'antd'
import { Text } from '@lmc/ui-kit/src/general'
import { getRequiredRule } from 'screens/Rostering/utils'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FinancialImpact, WhoCausedCancellation } from '@lmc/ts-common'

type CancelEventFormProps = {
  onFinish: (values: any) => void
  form: FormInstance<any>
}

enum CancellationReasonSU {
  HOSPITALISATION = 'Hospitalisation',
  HOLIDAYS = 'Holidays',
  NEXT_OF_KIN = 'Next of kin available for care',
  OTHER = 'Other',
}

enum CancellationReasonTM {
  SICKNESS = 'Sickness',
  CHILD_CARE = 'Child care',
  TRANSPORT = 'Transport related issues',
  OTHER = 'Other',
}

type SelectOption = {
  label: string
  value: string
}

export const CancelEventForm: FC<CancelEventFormProps> = ({
  onFinish,
  form,
}) => {
  const { t } = useTranslation()

  const [whoSelection, setWhoSelection] =
    useState<WhoCausedCancellation | null>(null)

  const generateSelectOptions = (
    enumObj: Record<string, string>,
  ): SelectOption[] => {
    return Object.values(enumObj).map((value) => ({
      value,
      label: value,
    }))
  }

  const whoCausedCancellationOptions = generateSelectOptions(
    WhoCausedCancellation,
  )

  const cancellationReasonSUOptions =
    generateSelectOptions(CancellationReasonSU)

  const cancellationReasonTMOptions =
    generateSelectOptions(CancellationReasonTM)

  const financialImpactOptions = generateSelectOptions(FinancialImpact)

  const handleWhoChange = (value: WhoCausedCancellation) => {
    setWhoSelection(value)
    form.setFieldsValue({ reason: undefined })
  }

  return (
    <Form form={form} onFinish={onFinish} layout="vertical">
      <FormItem
        name="whoCancelled"
        label={<Text>{t('WHO_CAUSED_CANCELLATION')}</Text>}
        rules={[getRequiredRule(t('WHO_CAUSED_CANCELLATION'))]}
        long
      >
        <Select
          options={whoCausedCancellationOptions}
          placeholder={t('PLEASE_SELECT')}
          onChange={handleWhoChange}
        />
      </FormItem>
      {whoSelection === WhoCausedCancellation.OTHER ? (
        <FormItem
          name="reason"
          label={<Text>{t('CANCELLATION_REASON')}</Text>}
          rules={[getRequiredRule(t('CANCELLATION_REASON'))]}
        >
          <TextArea placeholder={t('TYPE_HERE')} />
        </FormItem>
      ) : (
        <FormItem
          name="reason"
          label={<Text>{t('CANCELLATION_REASON')}</Text>}
          rules={[getRequiredRule(t('CANCELLATION_REASON'))]}
        >
          <Select
            options={
              whoSelection === WhoCausedCancellation.SERVICE_USER
                ? cancellationReasonSUOptions
                : cancellationReasonTMOptions
            }
            placeholder={t('PLEASE_SELECT')}
            disabled={!whoSelection}
          />
        </FormItem>
      )}
      <FormItem
        name="financialImpact"
        label={<Text>{t('FINANCIAL_IMPACT')}</Text>}
        rules={[getRequiredRule(t('FINANCIAL_IMPACT'))]}
      >
        <Select
          options={financialImpactOptions}
          placeholder={t('PLEASE_SELECT')}
        />
      </FormItem>
      <FormItem name="preventable" valuePropName="checked">
        <Checkbox>
          <StyledText>{t('CANCELLATION_PREVENTED')}</StyledText>
        </Checkbox>
      </FormItem>
      <FormItem name="notes" label={<Text>{t('NOTES')}</Text>}>
        <TextArea placeholder={t('TYPE_HERE')} maxLength={200} showCount />
      </FormItem>
    </Form>
  )
}

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
`
